/**
 * 图表时间轴导航的边界判定。
 *
 * lightweight-charts 没有内置导航 UI，这里自建缩放/平移按钮，因此需要自行
 * 约束两侧边界。判定集中在本文件，按钮与拖拽订阅共用同一套逻辑，避免两处跑偏。
 *
 * 量纲（lightweight-charts v5）：
 *   scrollPosition() = 右边缘到「最新一根柱」的根数
 *   实时位置（scrollToRealTime 的落点）= rightOffset
 *   < rightOffset → 视窗右缘落在末柱之前，即**在看更早的数据**
 *   > rightOffset → 末柱向左漂，右侧堆出空白
 *
 * 所以「左移」是减小 scrollPosition，「右移」是增大，与直觉方向相反。
 */

/** 边界迟滞阈值：浮点误差下避免在边界反复触发钳制 */
export const NAV_EPS = 1e-3;

/**
 * 平移一步后的 scrollPosition。
 *
 * @param dir -1 = 左移（看更早），+1 = 右移（回到更新）
 */
export function stepPosition(pos: number, dir: -1 | 1, rightOffset: number): number {
  // 右移不得超过实时位置（否则末柱右侧会出现空白）
  return Math.min(rightOffset, pos + dir);
}

/**
 * 计算钳制后的 scrollPosition；无需调整时返回 null。
 *
 * 左边界仅在「确实在看更早的数据」时生效：实时位置下把图缩到比全部数据还宽，
 * 视窗必然越过首根柱，此时空白应留在左侧（与缩放语义一致），
 * 若强行钳左边界会把视图往右猛拽，反而在右侧堆出空白。
 *
 * @param from 当前可见范围的左缘逻辑索引，null 表示取不到（无数据）
 */
export function resolveClampPosition(
  pos: number,
  from: number | null,
  rightOffset: number,
  eps: number = NAV_EPS,
): number | null {
  // 右侧越界：末柱左边出现空白，弹回实时位置
  if (pos > rightOffset + eps) return rightOffset;
  // 实时位置：不钳左侧，留白交给缩放
  if (pos >= rightOffset - eps) return null;
  // 左侧越界：首根柱脱离左边缘，按越界量推回
  if (from !== null && from < -eps) return pos - from;
  return null;
}

/**
 * 就地钳制时间轴。返回是否发生了移动。
 *
 * 覆盖拖拽、滚轮缩放与按钮三条路径；调用后两侧都不会越界。
 */
export function clampTimeScale(ts: {
  scrollPosition(): number;
  scrollToPosition(position: number, animated: boolean): void;
  options(): { rightOffset: number };
  getVisibleLogicalRange(): { from: number; to: number } | null;
}): boolean {
  const pos = ts.scrollPosition();
  const target = resolveClampPosition(
    pos,
    ts.getVisibleLogicalRange()?.from ?? null,
    ts.options().rightOffset,
  );
  if (target === null) return false;
  ts.scrollToPosition(target, false);
  return true;
}